import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { companyInfo } from '../data';
import { imageUrl } from '../lib/images';

export default function Blikkenslager() {
  const location = useLocation();
  const isEn = location.pathname.startsWith('/en');
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  const content = {
    da: {
      metaTitle: 'Blikkenslager Billund – Tagrender, zink & inddækninger | Billund VVS',
      metaDesc:
        'Autoriseret blikkenslager i Billund og omegn. Vi udfører tagrender, nedløb, skorstensinddækninger, kviste og zinkarbejde med højeste faglighed. Få et gratis tilbud!',
      canonical: 'https://billundvvs.netlify.app/blikkenslager/',
      badge: 'Autoriseret blikkenslager siden 1963 • TEKNIQ Garanti',
      heroH1: 'Professionelt blikkenslagerarbejde',
      heroH2: 'Din lokale VVS\'er og blikkenslager siden 1963',
      heroLead:
        'Billund Varme & Sanitet A/S er en veletableret VVS- og blikkenslagervirksomhed med mange års erfaring og stærke lokale rødder i Billund. Vi udfører blik-, zink- og smedearbejde for både private, virksomheder og offentlige kunder – altid med fokus på kvalitet, god service og holdbare løsninger.',
      ctaQuote: 'Kontakt os i dag',
      ctaCall: 'Ring 75 33 12 76',

      // Blueprint C: Feature Strip (Trust Badges)
      features: [
        {
          icon: 'fa-solid fa-cloud-showers-heavy',
          title: 'Tagrender og nedløb',
          desc: 'Montering, udskiftning og reparation.',
        },
        {
          icon: 'fa-solid fa-house-chimney',
          title: 'Inddækninger',
          desc: 'Skorstene, kviste, ovenlys og stern.',
        },
        {
          icon: 'fa-solid fa-gem',
          title: 'Zink & kobber',
          desc: 'Holdbare metaller med lang levetid.',
        },
        {
          icon: 'fa-solid fa-certificate',
          title: 'Faguddannet ekspertise',
          desc: 'Autoriseret arbejde med TEKNIQ Garanti.',
        },
      ],

      // Blueprint D: Gallery Showcase Grid (En alsidig blikkenslager)
      galleryTitle: 'En alsidig blikkenslager',
      gallerySubtitle:
        'Vi udfører alle former for blik-, zink- og smedearbejde – fra mindre opgaver til store projekter.',
      galleryItems: [
        {
          title: 'Tagrender og nedløb',
          desc: 'Zinktagrender med nedløb',
          detail: 'Holdbare tagrender i halvrund zink, kobber eller stål, der effektivt bortleder regnvand og pryder facaden.',
          image: '/_assets/tagrender-nedloeb-zink.jpg',
        },
        {
          title: 'Skorstensinddækning',
          desc: 'Tæt og præcis zinkinddækning',
          detail: '100% vandtæt inddækning af skorstene med loddede zinkfalser, der forhindrer fugtskader i tagkonstruktionen.',
          image: '/_assets/skorstensinddaekning-zink.jpg',
        },
        {
          title: 'Kvistbeklædning',
          desc: 'Smukke zinkkviste i stående fals',
          detail: 'Moderne og klassisk kvistbeklædning i zink, der modstår det danske klima og patinerer smukt over tid.',
          image: '/_assets/kvistbeklaedning-zink.jpg',
        },
        {
          title: 'Stern og gavle i zink',
          desc: 'Vedligeholdelsesfri zinkbeklædning',
          detail: 'Beskyt træværket mod råd og svamp med elegante zinkkapslinger, sternbrædder og vindskeder.',
          image: '/_assets/stern-gavle-zink.jpg',
        },
      ],

      // Vores værdier (Trust block matching reference)
      valuesTitle: 'Vores værdier',
      valuesSubtitle: 'Disse værdier driver vores arbejde hver dag.',
      values: [
        {
          icon: 'fa-solid fa-shield-halved',
          title: 'Pålidelighed',
          desc: 'Vi overholder aftaler og leverer til tiden.',
        },
        {
          icon: 'fa-solid fa-gear',
          title: 'Faglighed',
          desc: 'Vi holder os opdateret med ny viden og teknologi.',
        },
        {
          icon: 'fa-solid fa-users',
          title: 'Samarbejde',
          desc: 'Vi arbejder tæt sammen med kunder og rådgivere.',
        },
        {
          icon: 'fa-solid fa-leaf',
          title: 'Bæredygtighed',
          desc: 'Vi rådgiver om langtidsholdbare og miljøvenlige løsninger.',
        },
      ],

      // Craftsmanship Services
      servicesSectionTitle: 'Vores ydelser inden for blik og zink',
      servicesSectionLead:
        'Fra mindre udbedringer og service til store entrepriseopgaver ved nybyggeri og tagrenovering.',
      services: [
        {
          icon: 'fa-solid fa-ruler-combined',
          title: 'Tagrender & nedløbsrør',
          text: 'Vi leverer og monterer zink- og kobbertagrender, der loddes på stedet for maksimal tæthed. Vi udskifter slidte render og dimensionerer nedløb, så dit hus kan modstå selv kraftige skybrud.',
          bullets: ['Loddede zinktagrender med lang holdbarhed', 'Kobbertagrender til klassiske ejendomme', 'Rensning, reparation og frostsikring'],
        },
        {
          icon: 'fa-solid fa-shield-halved',
          title: 'Inddækninger & skotrender',
          text: 'Utætte inddækninger er en af de hyppigste årsager til skjulte råd- og fugtskader. Vi fremstiller skræddersyede inddækninger til skorstene, kviste, ovenlysvinduer og murkroner.',
          bullets: ['Skorstensinddækninger og sålbænke', 'Ventilationshætter og aftræk', 'Skotrender og fodblik'],
        },
        {
          icon: 'fa-solid fa-building',
          title: 'Zinkfacader & tagbeklædning',
          text: 'Zink er et unikt og eksklusivt materiale til både tagflader og facader. Det kræver ingen vedligeholdelse og danner en naturlig beskyttende patina, som modstår regn, blæst og sollys.',
          bullets: ['Stående fasede zinkpaneler', 'Facadebeklædning på nybyg og tilbyg', 'Kvisttage og gavlspidser'],
        },
        {
          icon: 'fa-solid fa-hammer',
          title: 'Specialprofiler & smedearbejde',
          text: 'Vores værksted bukker og klipper profiler præcist efter dine mål. Vi løser unikke detaljer til fredede bygninger såvel som hypermoderne arkitektur i tæt samarbejde med tømrere og arkitekter.',
          bullets: ['Eget værksted til bukning af profiler', 'Smedearbejde og rørføringer i stål', 'Kombineret VVS- og blikservice'],
        },
      ],

      // Why Choose Us
      benefitsTitle: 'Hvorfor vælge Billund Varme & Sanitet til blikarbejde?',
      benefits: [
        {
          icon: 'fa-solid fa-award',
          title: '60+ års erfaring',
          desc: 'Vi har udført VVS- og blikkenslagerarbejde i lokalområdet siden 1963. Det er din garanti for gennemprøvet faglighed.',
        },
        {
          icon: 'fa-solid fa-screwdriver-wrench',
          title: 'Rigtigt håndværk – loddet og tilpasset',
          desc: 'Vi samler og lodder zink efter de stolte håndværkstraditioner, så samlingerne forbliver tætte i årtier frem.',
        },
        {
          icon: 'fa-solid fa-handshake',
          title: 'Tæt samarbejde med tømrer',
          desc: 'Skal du have nyt tag? Vi koordinerer direkte med din tømrer, så blikarbejdet udføres smidigt, mens stilladset står.',
        },
        {
          icon: 'fa-solid fa-shield-check',
          title: 'TEKNIQ Garantiordning',
          desc: 'Som medlem af TEKNIQ Arbejdsgiverne er dit projekt dækket af garantiordningen for op til 150.000 kr.',
        },
      ],

      // Blueprint E: Dark Section / Bottom CTA Banner
      darkCtaTitle: 'Blikkenslagerarbejde til både private og erhverv',
      darkCtaText:
        'Vi rådgiver dig om den bedste løsning, der passer til din bygning og dit behov. Uanset om det gælder udskiftning af et par tagrender eller en komplet facadebeklædning i zink.',
      darkCtaBtn: 'Kontakt os i dag',

      // FAQ
      faqTitle: 'Ofte stillede spørgsmål om blik og zink',
      faqs: [
        {
          q: 'Hvor længe holder tagrender i zink?',
          a: 'Kvalitetstagrender i zink, der er korrekt loddet og monteret af en fagmand, har en typisk levetid på 30 til 50 år eller mere. Zink udvikler med tiden en naturlig patina (zinkkarbonat), som beskytter metallet mod yderligere nedbrydning.',
        },
        {
          q: 'Hvad er forskellen på zink og plasttagrender?',
          a: 'Zink er et naturligt og genanvendeligt metal med markant længere holdbarhed end plast. Zink tåler frost, sollys og temperatursvingninger uden at blive skørt eller blegne, og det giver huset et eksklusivt, gedigent udtryk.',
        },
        {
          q: 'Kan I reparere en utæt tagrende, eller skal den skiftes?',
          a: 'Hvis utætheden skyldes en enkelt knækket lodning eller et lokalt hul, kan vores blikkenslager ofte efterlodde og udbedre skaden. Er tagrenden generelt gennemtæret eller slidt op, anbefaler vi en hel eller delvis udskiftning for at undgå fugtskader.',
        },
        {
          q: 'Kører I også ud til blikkenslageropgaver i Grindsted, Give og Vejle?',
          a: 'Ja, vi dækker hele Billund Kommune samt nabobyer som Grindsted, Give, Sdr. Omme, Vorbasse og Vejle. Vi kommer gerne ud og besigtiger dit tag eller dine tagrender uden beregning.',
        },
      ],
    },

    en: {
      metaTitle: 'Sheet Metal & Tinsmith Billund – Gutters & Zinc Work | Billund VVS',
      metaDesc:
        'Certified sheet metal craftsmanship in Billund and surrounding areas. Gutters, downpipes, chimney flashings, dormers and zinc facade work. Get a free quote!',
      canonical: 'https://billundvvs.netlify.app/en/sheet-metal/',
      badge: 'Certified Sheet Metal Masters since 1963 • TEKNIQ Guarantee',
      heroH1: 'Professional sheet metal craft',
      heroH2: 'Your local plumber and tinsmith since 1963',
      heroLead:
        'Billund Varme & Sanitet A/S is an established craftsman company with many years of experience and strong local roots in Billund. We carry out sheet metal, zinc, and metalwork for private homeowners, commercial clients, and public facilities – always focused on quality, good service, and durable solutions.',
      ctaQuote: 'Contact us today',
      ctaCall: 'Call 75 33 12 76',

      // Blueprint C: Feature Strip (Trust Badges)
      features: [
        {
          icon: 'fa-solid fa-cloud-showers-heavy',
          title: 'Gutters and downpipes',
          desc: 'Installation, replacement, and repair.',
        },
        {
          icon: 'fa-solid fa-house-chimney',
          title: 'Flashings & valleys',
          desc: 'Chimneys, dormers, skylights, and fascia.',
        },
        {
          icon: 'fa-solid fa-gem',
          title: 'Zinc & copper',
          desc: 'Durable metals with extraordinary lifespan.',
        },
        {
          icon: 'fa-solid fa-certificate',
          title: 'Certified expertise',
          desc: 'Authorised trade work with TEKNIQ Guarantee.',
        },
      ],

      // Blueprint D: Gallery Showcase Grid
      galleryTitle: 'A versatile sheet metal workshop',
      gallerySubtitle:
        'We undertake all forms of sheet metal and zinc craftsmanship – from minor repairs to large contracts.',
      galleryItems: [
        {
          title: 'Gutters and downpipes',
          desc: 'Zinc gutters with downpipes',
          detail: 'Durable half-round zinc, copper, or steel gutters that protect walls and foundation from water intrusion.',
          image: '/_assets/tagrender-nedloeb-zink.jpg',
        },
        {
          title: 'Chimney flashing',
          desc: 'Watertight and clean zinc flashing',
          detail: '100% watertight chimney flashings with soldered seams to prevent leaks in your attic and roofing.',
          image: '/_assets/skorstensinddaekning-zink.jpg',
        },
        {
          title: 'Dormer cladding',
          desc: 'Elegant zinc dormers in standing seam',
          detail: 'Contemporary and traditional dormer cladding in zinc that patinates gracefully and requires zero maintenance.',
          image: '/_assets/kvistbeklaedning-zink.jpg',
        },
        {
          title: 'Fascia and gables in zinc',
          desc: 'Maintenance-free zinc cladding',
          detail: 'Protect vulnerable exterior woodwork against rot and weathering with bespoke zinc caps and covers.',
          image: '/_assets/stern-gavle-zink.jpg',
        },
      ],

      // Values section
      valuesTitle: 'Our values',
      valuesSubtitle: 'These values drive our work every day.',
      values: [
        {
          icon: 'fa-solid fa-shield-halved',
          title: 'Reliability',
          desc: 'We honor agreements and deliver on time.',
        },
        {
          icon: 'fa-solid fa-gear',
          title: 'Craftsmanship',
          desc: 'We stay updated with new methods and technology.',
        },
        {
          icon: 'fa-solid fa-users',
          title: 'Collaboration',
          desc: 'We work closely with clients and partners.',
        },
        {
          icon: 'fa-solid fa-leaf',
          title: 'Sustainability',
          desc: 'We advise on energy-efficient and durable solutions.',
        },
      ],

      // Craftsmanship Services
      servicesSectionTitle: 'Our sheet metal and tinsmith services',
      servicesSectionLead:
        'From minor repairs to major new construction and roof renovation projects across the region.',
      services: [
        {
          icon: 'fa-solid fa-ruler-combined',
          title: 'Gutters & downpipes',
          text: 'We supply and fit premium zinc and copper gutters soldered on site for maximum watertightness and long lifespan.',
          bullets: ['Soldered zinc gutters with decades of durability', 'Copper drainage for heritage buildings', 'Cleaning, repair, and frost protection'],
        },
        {
          icon: 'fa-solid fa-shield-halved',
          title: 'Flashings & roof valleys',
          text: 'Leaking flashings cause expensive moisture damage. We tailor custom flashings for chimneys, dormers, skylights, and parapets.',
          bullets: ['Chimney flashings and window sills', 'Ventilation caps and pipe penetrations', 'Roof valleys and drip edges'],
        },
        {
          icon: 'fa-solid fa-building',
          title: 'Zinc facades & roof panels',
          text: 'Zinc is an exceptional architectural material. It requires zero paint or maintenance and patinates naturally over time.',
          bullets: ['Standing seam panels', 'Cladding on contemporary extensions', 'Dormer roofs and architectural details'],
        },
        {
          icon: 'fa-solid fa-hammer',
          title: 'Custom sheet bending & fabrication',
          text: 'Our in-house workshop folds and cuts custom profiles to your exact measurements, working seamlessly with carpenters and architects.',
          bullets: ['Workshop for bespoke profile bending', 'Steel pipework and brackets', 'Combined plumbing and sheet metal contracts'],
        },
      ],

      // Why Choose Us
      benefitsTitle: 'Why choose Billund Varme & Sanitet for sheet metal work?',
      benefits: [
        {
          icon: 'fa-solid fa-award',
          title: '60+ years of local experience',
          desc: 'We have carried out plumbing and sheet metal work in Billund since 1963. Reliable, proven craftmanship.',
        },
        {
          icon: 'fa-solid fa-screwdriver-wrench',
          title: 'Authentic craftsmanship – soldered & fitted',
          desc: 'We assemble and solder zinc with time-honoured techniques, ensuring seams stay dry for decades.',
        },
        {
          icon: 'fa-solid fa-handshake',
          title: 'Close collaboration with roofers',
          desc: 'Replacing your roof? We coordinate directly with your carpenter so metalwork is completed while scaffolding stands.',
        },
        {
          icon: 'fa-solid fa-shield-check',
          title: 'TEKNIQ Guarantee scheme',
          desc: 'All work is covered by the TEKNIQ guarantee scheme up to DKK 150,000 for your peace of mind.',
        },
      ],

      // Blueprint E: Dark Section / Bottom CTA Banner
      darkCtaTitle: 'Sheet metal work for homeowners and commercial clients',
      darkCtaText:
        'We advise you on the optimal solution tailored to your building and specific requirements. From replacing gutters to complete zinc facade cladding.',
      darkCtaBtn: 'Contact us today',

      // FAQ
      faqTitle: 'Frequently asked questions about sheet metal and zinc',
      faqs: [
        {
          q: 'How long do zinc gutters last?',
          a: 'High quality zinc gutters that are properly soldered and mounted by a trained craftsman typically last 30 to 50 years or more. Zinc develops a protective patina that prevents corrosion.',
        },
        {
          q: 'What is the advantage of zinc over plastic?',
          a: 'Zinc is an authentic, recyclable metal that does not become brittle in frost or fade under UV light. It adds high architectural value and timeless elegance to any home.',
        },
        {
          q: 'Can you repair a leaking gutter, or does it need full replacement?',
          a: 'If the leak is due to a cracked solder joint or localized damage, our craftsman can often re-solder and repair it on site. If the metal is extensively worn through, replacement is recommended.',
        },
        {
          q: 'Do you work in Grindsted, Give, and Vejle as well?',
          a: 'Yes, we service the entire Billund municipality plus neighboring towns including Grindsted, Give, Sdr. Omme, Vorbasse, and Vejle. We provide free site inspections and quotes.',
        },
      ],
    },
  };

  const c = isEn ? content.en : content.da;

  return (
    <div className="bg-white text-content-body font-sans">
      <Helmet>
        <html lang={isEn ? 'en' : 'da'} />
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDesc} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDesc} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/tagrender-nedloeb-zink.jpg" />
        <link rel="canonical" href={c.canonical} />
      </Helmet>

      {/* =========================================================================
          HERO SECTION (High-contrast clean layout with cover image & gradient flow)
          ========================================================================= */}
      <section className="relative overflow-hidden bg-white border-b border-gray-100 py-10 sm:py-16 lg:py-20 min-h-[460px] lg:min-h-[520px] flex items-center">
        {/* Right side cover image with smooth gradient flow */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-1/2 xl:w-[50%] 2xl:w-[52%] h-full pointer-events-none select-none overflow-hidden">
          <img
            src={imageUrl('/_assets/tagrender-nedloeb-zink.jpg', 'width=1400,quality=90,format=auto')}
            alt={isEn ? 'Close-up of zinc gutter and downspout' : 'Nærbillede af zinktagrende og nedløbsrør'}
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          {/* Gentle edge gradient transition from white text container into image */}
          <div className="absolute inset-y-0 left-0 w-44 bg-gradient-to-r from-white via-white/80 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white/20 to-transparent" />
          
          {/* Subtle certification pill in top/bottom area */}
          <div className="absolute bottom-5 right-6 pointer-events-auto bg-white/90 backdrop-blur-xs px-3.5 py-1.5 rounded-full shadow-xs border border-white/60 flex items-center gap-2 text-xs font-semibold text-[#09263f]">
            <i className="fa-solid fa-certificate text-[#d11218]" aria-hidden="true"></i>
            <span>TEKNIQ Autoriseret Installatør 2026–2028</span>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-xl lg:max-w-xl">
            <h1 className="text-4xl md:text-5xl font-extrabold text-[#09263f] tracking-tight leading-tight">
              {c.heroH1}
            </h1>
            <p className="text-xl md:text-2xl font-bold text-[#09263f] mt-2 leading-snug">
              {c.heroH2}
            </p>
            <p className="text-base leading-relaxed text-[#334155] mt-4">
              {c.heroLead}
            </p>

            {/* Action Button CTA (Pattern A) */}
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Link
                to={isEn ? '/en/contact' : '/kontakt'}
                className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#d11218] hover:bg-[#b30f14] text-white font-bold text-base rounded-[6px] transition-colors duration-150 group shadow-sm"
              >
                <span>{c.ctaQuote} &rarr;</span>
              </Link>
              <a
                href="tel:+4575331276"
                className="inline-flex items-center gap-2 px-5 py-3.5 bg-[#f4f6f8] hover:bg-[#e5e9ee] text-[#09263f] font-bold text-base rounded-[6px] transition-colors duration-150"
              >
                <i className="fa-solid fa-phone text-sm text-[#d11218]" aria-hidden="true"></i>
                <span>{c.ctaCall}</span>
              </a>
            </div>

            {/* Mobile & Tablet fallback image */}
            <div className="mt-8 lg:hidden">
              <div className="relative aspect-[16/10] w-full overflow-hidden rounded-[8px] bg-[#e5e9ee] shadow-sm border border-[#e5e9ee]">
                <img
                  src={imageUrl('/_assets/tagrender-nedloeb-zink.jpg', 'width=1000,quality=85,format=auto')}
                  alt={isEn ? 'Close-up of zinc gutter and downspout' : 'Nærbillede af zinktagrende og nedløbsrør'}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-xs text-white px-1">
                  <span className="inline-flex items-center gap-1.5 font-medium drop-shadow-xs">
                    <i className="fa-solid fa-certificate text-white" aria-hidden="true"></i>
                    <span>TEKNIQ 2026–2028</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          PATTERN C: TRUST / FEATURE INDICATOR BAR
          ========================================================================= */}
      <div className="bg-white py-6 border-y border-[#e5e9ee]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {c.features.map((feature, idx) => (
            <div key={idx} className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-full bg-[#09263f] text-white flex items-center justify-center shrink-0">
                <i className={`${feature.icon} text-lg`} aria-hidden="true"></i>
              </div>
              <div>
                <h4 className="text-sm md:text-[15px] font-extrabold text-[#09263f] leading-tight">
                  {feature.title}
                </h4>
                <p className="text-xs text-[#64748b] mt-0.5 leading-snug">
                  {feature.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* =========================================================================
          PATTERN D: IMAGE GRID CARD (En alsidig blikkenslager)
          ========================================================================= */}
      <section className="py-12 md:py-16 bg-white border-b border-[#e5e9ee]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#09263f]">
              {c.galleryTitle}
            </h2>
            <p className="mt-1.5 text-base text-[#334155]">
              {c.gallerySubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.galleryItems.map((item, idx) => (
              <div key={idx} className="flex flex-col group">
                <div className="aspect-[4/3] w-full overflow-hidden rounded-[8px] bg-[#e5e9ee] shadow-sm">
                  <img
                    src={imageUrl(item.image, 'width=600,quality=80,format=auto')}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="mt-3">
                  <h3 className="text-base font-bold text-[#09263f]">
                    {item.title}
                  </h3>
                  <p className="text-xs md:text-sm text-[#64748b] mt-0.5">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION: VORES VÆRDIER (Matches reference design)
          ========================================================================= */}
      <section className="py-12 md:py-16 bg-white border-b border-[#e5e9ee]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#09263f]">
              {c.valuesTitle}
            </h2>
            <p className="mt-1.5 text-base text-[#334155]">
              {c.valuesSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.values.map((val, idx) => (
              <div key={idx} className="flex flex-col">
                <div className="w-12 h-12 rounded-full bg-[#09263f] text-white flex items-center justify-center shrink-0">
                  <i className={`${val.icon} text-lg`} aria-hidden="true"></i>
                </div>
                <h4 className="text-sm md:text-[15px] font-extrabold text-[#09263f] mt-3 leading-tight">
                  {val.title}
                </h4>
                <p className="text-xs md:text-sm text-[#334155] mt-1 leading-snug">
                  {val.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================================
          DETAILED CRAFTSMANSHIP SERVICES (In-depth cards)
          ========================================================================= */}
      <section className="py-12 md:py-16 bg-[#f4f6f8] border-b border-[#e5e9ee]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#09263f]">
              {c.servicesSectionTitle}
            </h2>
            <p className="mt-2 text-base text-[#334155]">
              {c.servicesSectionLead}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {c.services.map((service, idx) => (
              <div
                key={idx}
                className="bg-white border border-[#e5e9ee] rounded-[8px] p-6 sm:p-8 shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center gap-3.5 mb-4">
                    <div className="w-11 h-11 rounded-full bg-[#09263f] text-white flex items-center justify-center shrink-0">
                      <i className={`${service.icon} text-base`} aria-hidden="true"></i>
                    </div>
                    <h3 className="text-lg font-bold text-[#09263f]">{service.title}</h3>
                  </div>

                  <p className="text-sm text-[#334155] leading-relaxed mb-5">{service.text}</p>

                  <ul className="space-y-2 mb-6">
                    {service.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-[#334155]">
                        <i className="fa-solid fa-check text-[#d11218] text-xs mt-1 shrink-0" aria-hidden="true"></i>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 border-t border-[#e5e9ee]">
                  <Link
                    to={isEn ? '/en/contact' : '/kontakt'}
                    className="text-xs sm:text-sm font-bold text-[#09263f] hover:text-[#d11218] inline-flex items-center gap-1.5 transition-colors"
                  >
                    <span>{isEn ? 'Request quote for this' : 'Indhent tilbud på dette'} &rarr;</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================================
          BLUEPRINT E: SPLIT DARK-IMAGE CTA SECTION
          ========================================================================= */}
      <section className="relative bg-[#051624] overflow-hidden py-14 md:py-20 text-white">
        {/* Background image with gradient overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/solrig-industriga-rd-med-ra-de-porte-og-varevogne.png"
            alt="Billund Varme & Sanitet domicil på Montanavej 1B i Billund"
            className="w-full h-full object-cover object-center"
          />
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, #051624 0%, #051624 22%, rgba(5, 22, 36, 0.85) 35%, rgba(5, 22, 36, 0.4) 45%, rgba(5, 22, 36, 0.08) 54%, rgba(5, 22, 36, 0) 62%)'
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight">
              {c.darkCtaTitle}
            </h2>
            <p className="mt-4 text-sm md:text-base text-slate-300 leading-relaxed">
              {c.darkCtaText}
            </p>
            <div className="mt-6">
              <a
                href="tel:+4575331276"
                className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#d11218] hover:bg-[#b30f14] text-white font-bold text-base rounded-[6px] transition-colors duration-150 group shadow-sm"
              >
                <span>{isEn ? 'Call 75 33 12 76 →' : 'Ring 75 33 12 76 →'}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          FAQ SECTION
          ========================================================================= */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-brand-800 tracking-tight">
              {c.faqTitle}
            </h2>
            <p className="mt-2 text-sm text-content-body">
              {isEn
                ? 'Answers to common questions about zinc lifespan, repairs, and coverage.'
                : 'Få svar på de mest almindelige spørgsmål om zinktagrender, holdbarhed og priser.'}
            </p>
          </div>

          <div className="space-y-3">
            {c.faqs.map((faq, idx) => (
              <div
                key={idx}
                className="border border-slate-200 rounded-xl overflow-hidden bg-white transition-colors"
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(idx)}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 font-bold text-sm sm:text-base text-brand-800 hover:text-accent-500 transition-colors"
                  aria-expanded={openFaq === idx}
                >
                  <span>{faq.q}</span>
                  <i
                    className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 text-slate-400 ${
                      openFaq === idx ? 'rotate-180 text-accent-500' : ''
                    }`}
                    aria-hidden="true"
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-5 text-sm text-content-body leading-relaxed border-t border-slate-100 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Bottom Help callout */}
          <div className="mt-10 p-6 rounded-xl bg-slate-50 border border-slate-200 text-center">
            <p className="text-sm font-semibold text-brand-800">
              {isEn
                ? 'Need guidance on a specific sheet metal project?'
                : 'Har du et konkret blikkenslagerprojekt eller spørgsmål til dit tag?'}
            </p>
            <p className="text-xs text-content-muted mt-1">
              {isEn
                ? 'Call our workshop directly on 75 33 12 76 or send us photos for an estimate.'
                : 'Ring direkte til vores værksted på 75 33 12 76 eller send billeder for en vurdering.'}
            </p>
            <div className="mt-4 flex justify-center gap-3">
              <Link
                to={isEn ? '/en/get-a-quote' : '/tilbud'}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-800 hover:bg-brand-900 text-white text-xs font-semibold transition-colors"
              >
                <span>{isEn ? 'Request quote' : 'Send forespørgsel'}</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
